import {Injectable} from "@angular/core";
import {HttpClient, HttpContext, HttpParams} from "@angular/common/http";
import {SILENT_HTTP_ERRORS} from "../../../../shared/interceptors/httpError.interceptor";
import {SubscriptionBillingInterface} from "../../../../shared/interfaces/subscription-billing.interface";
import {SubscriptionModuleEnum} from "../enums/subscription-module.enum";
import {CompanySubscriptionInterface} from "../interfaces/company-subscription.interface";
import {BasicFiltersInterface} from "../../../../shared/interfaces/basic-filters.interface";
import {ListingResponseInterface} from "../../../../shared/interfaces/listing-response.interface";
import {SubscriptionInvoiceInterface} from "../interfaces/subscription-invoice.interface";
import {Store} from "@ngrx/store";
import {selectCompany, selectSubscription} from "../../../../shared/store/store.selectors";
import {combineLatest, EMPTY, Observable, of, timer} from "rxjs";
import {catchError, exhaustMap, filter, last, map, switchMap, take, takeWhile} from "rxjs/operators";
import {CompanyService} from "../../../../shared/services/company.service";
import {CurrentRestaurantSubscriptionInterface} from "../interfaces/current-restaurant-subscription.interface";
import {GetSubscription} from "../../../../shared/store/store.actions";
import {CompanySubscriptionStatusEnum} from "../enums/company-subscription-status.enum";
import {SubscriptionBlikInterface, SubscriptionBlikPreviewInterface} from "../interfaces/subscription-blik.interface";

const BLIK_POLL_INTERVAL_MS = 2000;
const BLIK_POLL_MAX_ATTEMPTS = 60;

@Injectable({
    providedIn: 'root'
})
export class SubscriptionService {
    subscription: CurrentRestaurantSubscriptionInterface;
    constructor(
        private http: HttpClient,
        private store: Store,
        private companyService: CompanyService,
    ) {
        this.store.dispatch(GetSubscription());
        this.store.select(selectSubscription).subscribe(result => {
            this.subscription = result;
        })
    }

    /**
     * Emits once both the company and the current restaurant's subscription are loaded - pages opened
     * directly by URL render before that, so a hasAccess() check in ngOnInit would see no access yet.
     */
    whenLoaded(): Observable<void> {
        return combineLatest([this.store.select(selectCompany), this.store.select(selectSubscription)]).pipe(
            filter(([company, subscription]) => !!company && !!subscription),
            take(1),
            map(() => undefined)
        );
    }

    hasAccess(module: SubscriptionModuleEnum): boolean|number {
        if (!this.companyService.company) {
            return false;
        }

        if (this.companyService.company.trial_ends_at) {
            return true;
        }

        if (this.subscription && 'STATUS' in this.subscription) {
            if (this.subscription.STATUS === CompanySubscriptionStatusEnum.CANCELED) {
                return false;
            }
        }

        return this.hasAccessInCompanySubscription(module);
    }

    hasAccessInCompanySubscription(module: SubscriptionModuleEnum): boolean {
        if (module === SubscriptionModuleEnum.STOCK) {
            module = SubscriptionModuleEnum.INVENTORY;
        }

        if (module === SubscriptionModuleEnum.KDS_APP) {
            module = SubscriptionModuleEnum.KDS;
        }

        if (module === SubscriptionModuleEnum.LOYALTY_PROGRAM || module === SubscriptionModuleEnum.PROMO_CODES) {
            module = SubscriptionModuleEnum.LOYALTY_PROMO_CODES;
        }

        if (module === SubscriptionModuleEnum.QR_MENU || module === SubscriptionModuleEnum.TABLE_ORDERS) {
            module = SubscriptionModuleEnum.QR_MENU_TABLE_ORDER;
        }

        return this.subscription && this.subscription[module];
    }

    loadIntent() {
     return this.http.post<string>(`/api_company/subscription/intent`, {});
    }

    getInvoices(filters: BasicFiltersInterface) {
        const params = new HttpParams({
            fromObject: {
                page: filters.page.toString(),
                limit: filters.limit.toString(),
                sort_by: filters.sort_by,
                sort_dir: filters.sort_dir,
                search: filters.search,
            }
        });
        return this.http.get<ListingResponseInterface<SubscriptionInvoiceInterface>>(`/api_company/company-subscription/invoices`, {params});
    }

    updateCard(data) {
        return this.http.post<boolean>(`/api_company/subscription/card`, data);
    }

    createCompanySubscription(data) {
        return this.http.post(`/api_company/company-subscription`, data);
    }

    changeCompanySubscription(data) {
        return this.http.patch(`/api_company/company-subscription`, data);
    }

    changeCompanySubscriptionType(is_automatic_payment: boolean) {
        return this.http.patch(`/api_company/company-subscription/type`, {is_automatic_payment});
    }

    getBlikPreview() {
        return this.http.get<SubscriptionBlikPreviewInterface>(`/api_company/subscription/blik/preview`, {
            context: new HttpContext().set(SILENT_HTTP_ERRORS, true)
        });
    }

    registerBlik(blik_code: string) {
        return this.http.post<SubscriptionBlikInterface>(`/api_company/subscription/blik`, {blik_code});
    }

    getBlikStatus(id: number, silent = false) {
        return this.http.get<SubscriptionBlikInterface>(`/api_company/subscription/blik/${id}/status`, {
            context: new HttpContext().set(SILENT_HTTP_ERRORS, silent)
        });
    }

    registerBlikAndWait(blikCode: string): Observable<SubscriptionBlikInterface> {
        return this.registerBlik(blikCode).pipe(
            switchMap(alias => this.waitForBlikConfirmation(alias))
        );
    }

    waitForBlikConfirmation(alias: SubscriptionBlikInterface): Observable<SubscriptionBlikInterface> {
        if (!alias || alias.status !== 'PENDING') {
            return of(alias);
        }

        return timer(BLIK_POLL_INTERVAL_MS, BLIK_POLL_INTERVAL_MS).pipe(
            take(BLIK_POLL_MAX_ATTEMPTS),
            exhaustMap(() => this.getBlikStatus(alias.id, true).pipe(
                catchError(() => EMPTY)
            )),
            takeWhile(status => status.status === 'PENDING', true),
            last(null, alias)
        );
    }

    getCompanySubscription() {
        return this.http.get<CompanySubscriptionInterface>(`/api_company/company-subscription`);
    }

    cancelCompanySubscription() {
        return this.http.post<boolean>(`/api_company/company-subscription/cancel`, {});
    }

    updateRestaurantSubscriptionBilling(restaurantId: number, data: SubscriptionBillingInterface) {
        return this.http.patch<{ data: SubscriptionBillingInterface }>(`/api_company/restaurants/${restaurantId}/subscription-billing`, data);
    }

    deleteRestaurantSubscriptionBilling(restaurantId: number) {
        return this.http.delete<{ data: null }>(`/api_company/restaurants/${restaurantId}/subscription-billing`);
    }
}
